import { Capacitor } from "@capacitor/core";
import { Box } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { ActionFab } from "../../components/ActionFab";
import { ApprovalCelebrationModal } from "../../components/ApprovalCelebrationModal/ApprovalCelebrationModal";
import { usePerformGlobalRefresh } from "../../components/header/hooks";
import { PickupSuggestionDrawer } from "../../components/PickupSuggestionDrawer/PickupSuggestionDrawer";
import { PEEK_HEIGHT } from "../../components/UIToolkit/Popups/InHouseMobileSheet";
import { useAuth } from "../../contexts/AuthProvider";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useApprovalCelebration } from "../../hooks/useApprovalAcknowledgement";
import { useShowReviewPopup } from "../../hooks/useRatingPrompt";
import { trackEvent } from "../../utils/analytics";
import { getShiftDisplayDate } from "../../utils/dateUtils";
import { type OpenShift, type OpenShiftRequest, RequestStatus, type ShiftType } from "../../utils/models";
import { MobilePromotionBanner } from "../DaySchedule/MobilePromotionBanner";
import { NurseScheduleSection } from "./components/NurseScheduleSection";
import { useBedsideNurse } from "./hooks";

// Delay before the native review sheet is offered, giving the home screen time to settle first.
const REVIEW_POPUP_DELAY_MS = 2_000;

function useReviewPopup(isCelebrationOpen: boolean, areNotificationsLoading: boolean): void {
	const showReviewPopup = useShowReviewPopup();

	useEffect(() => {
		// Don't arm the timer until notifications load and the celebration modal is closed,
		// so the review sheet never appears on top of the celebration.
		if (areNotificationsLoading || isCelebrationOpen) {
			return;
		}

		const timerID = setTimeout(() => {
			showReviewPopup().catch(console.error);
		}, REVIEW_POPUP_DELAY_MS);

		return () => {
			clearTimeout(timerID);
		};
	}, [showReviewPopup, isCelebrationOpen, areNotificationsLoading]);
}

export const BedsideHome: FC = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { userShifts, scheduleBounds } = useBedsideNurse();

	const { approvals, isOpen: isApprovalCelebrationOpen, isLoading: areNotificationsLoading, dismiss: dismissApprovalCelebration } = useApprovalCelebration();
	useReviewPopup(isApprovalCelebrationOpen, areNotificationsLoading);

	const { userInfo } = useAuth();
	const { staffRequests } = useRequestsContext();
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const [allOpenShifts, setAllOpenShifts] = useState<Record<string, OpenShift[]>>({});
	const [isPeekVisible, setIsPeekVisible] = useState<boolean>(false);

	const handleBedsideRefresh = async (): Promise<void> => {
		try {
			trackEvent("bedside_pull_refresh_started", { nurseId: userInfo.id });
			await Promise.allSettled([performGlobalRefresh()]);
			trackEvent("bedside_pull_refresh_completed", { nurseId: userInfo.id });
		} catch (error) {
			trackEvent("bedside_pull_refresh_failed", {
				nurseId: userInfo.id,
				error: (error as Error).message,
			});
		}
	};

	useEffect(() => {
		if (staffRequests) {
			const openShiftsMap: Record<string, OpenShift[]> = {};
			staffRequests
				.filter((request): request is OpenShiftRequest => "needed_nurses" in request && request.status === RequestStatus.Active)
				.forEach((request) => {
					const date = request.message.date;
					const dateToUse = getShiftDisplayDate(date, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
					const dateKey = dateToUse.toFormat("yyyy-MM-dd");
					openShiftsMap[dateKey] ||= [];
					openShiftsMap[dateKey].push({
						id: request.id,
						shift_start: request.message.date,
						shift_end: request.message.date.plus({ hours: 12 }),
						shift_type: request.message.shift_type as ShiftType.Day | ShiftType.Night,
						needed_nurses: request.needed_nurses,
						created_by: Number(request.created_by),
						request_data: request.message.request_data,
						request_for: 0,
						location: request.location || (request.message.request_data as any)?.location || "",
						department_id: request.department_id,
					});
				});

			setAllOpenShifts(openShiftsMap);
		}
	}, [staffRequests, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes]);

	return (
		<>
			<Box
				data-testid="my-schedule-root"
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "100%",
					width: "100%",
					py: 2,
				}}
			>
				<NurseScheduleSection
					shifts={userShifts}
					scheduleBounds={scheduleBounds}
					allOpenShifts={allOpenShifts}
					staffScheduleDisplayUntilDate={chosenDepartment.staffScheduleDisplayUntilDate}
					onRefresh={handleBedsideRefresh}
				/>
				<ActionFab bottomOffset={isPeekVisible ? PEEK_HEIGHT : 0} />
				<PickupSuggestionDrawer onPeekVisibleChange={setIsPeekVisible} />
			</Box>
			{!Capacitor.isNativePlatform() && <MobilePromotionBanner />}
			<ApprovalCelebrationModal open={isApprovalCelebrationOpen} approvals={approvals} onClose={dismissApprovalCelebration} />
		</>
	);
};
